import type { FormSubmissionGetListRequestSchema } from "../schemas/FormSubmissionSchemas.ts";
import type { ReportWithDownloadV2Type } from "../schemas/ReportSchemas.ts";
import {
  type TriggerWorkflowType,
  type WorkflowFormSubmissionType,
  type WorkflowType,
} from "../schemas/WorkflowSchemas.ts";
import type {
  FilterObjectType,
  GetListRequestType,
  GetListResponseType,
  ResourceAdapterType,
} from "../util/index.ts";
import { getFilterObject } from "../util/index.ts";
import { restAPI } from "./api.ts";

export type WorkflowsGetListFilterType = FilterObjectType<
  {
    "workflow_category.id": string;
    "workflow_template.id": string;
    "workflow_template.description": string;
    "workflow_template.api_version": string | { $in: string[] };
    "facility.id": string;
    "facility.status": string;
    "facility.custom_attributes": Record<string, string>;
    dataset_id: string | { $in: string[] } | { $exists: boolean };
  } & Pick<
    WorkflowType,
    | "id"
    | "workflow_template_id"
    | "status"
    | "due_date"
    | "created_by"
    | "created_at"
    | "asset_id"
    | "time_period"
    | "duration"
  >
>;

export const WorkflowAdapter: WorkflowAdapterType = {
  /**
   * Get the details of a single workflow
   * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflows/get_workflow}
   */
  getOne: async ({ id }) => {
    const resp = await restAPI.nodeAPI.GET<WorkflowType>({
      endpoint: `/workflows/${id}`,
    });

    return {
      data: resp,
    };
  },
  /**
   * Get all workflows
   * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflows/search_workflows}
   */
  getList: async ({
    page,
    pageSize,
    sortBy,
    sortDirection,
    groupBy,
    filters,
  }) =>
    restAPI.nodeAPI.POST<GetListResponseType<WorkflowType>>({
      endpoint: "/workflows/search",
      body: {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_direction: sortDirection,
        group_by: groupBy,
        filter: getFilterObject(filters),
      },
    }),
  getMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  createOne: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  createMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  /**
   * Trigger a workflow
   * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflows/create_workflow}
   */
  trigger: ({
    workflow_template_id,
    asset_id,
    time_period,
    duration,
    variables,
  }) =>
    restAPI.nodeAPI.POST({
      endpoint: "/workflows",
      body: {
        workflow_template_id,
        asset_id,
        ...(time_period && duration
          ? { due_date: { time_period, duration } }
          : {}),
        ...(variables ? { variables } : {}),
      },
    }),
  /**
   * Delete a single workflow
   * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflows/delete_workflow}
   */
  deleteOne: ({ id }) => {
    return restAPI.nodeAPI.DELETE({
      endpoint: `/workflows/${id}`,
    });
  },
  deleteMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  /**
   * update a single workflow
   * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflows/put_workflow}
   */
  updateOne: async ({ id, data }) => {
    const { status, dismissal_reason, dismissed_at } = data;

    const resp = await restAPI.nodeAPI.PUT<WorkflowType>({
      endpoint: `/workflows/${id}`,
      body: {
        status,
        ...(dismissal_reason !== undefined ? { dismissal_reason } : {}),
        ...(dismissed_at !== undefined ? { dismissed_at } : {}),
      },
    });

    return {
      data: resp,
    };
  },
  updateMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  forms: {
    /**
     * list of related form submissions to the workflow
     * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflows/search_workflow_form_submissions}
     */
    getList: ({ workflowId, page, pageSize, sortBy, sortDirection, filters }) =>
      restAPI.nodeAPI.POST<GetListResponseType<WorkflowFormSubmissionType>>({
        endpoint: `/workflows/${workflowId}/form_submissions/search`,
        body: {
          page,
          page_size: pageSize,
          sort_by: sortBy,
          sort_direction: sortDirection,
          filter: getFilterObject(filters),
        },
      }),
  },
  /**
   * Export workflows as a downloadable report
   * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflows/export_workflows}
   */
  exportList: ({ filters, sortBy, sortDirection }: GetListRequestType) =>
    restAPI.nodeAPI.POST<ReportWithDownloadV2Type>({
      endpoint: "/workflows/export",
      body: {
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: getFilterObject(filters),
      },
    }),
};

export interface WorkflowAdapterType extends ResourceAdapterType<WorkflowType> {
  trigger: (args: TriggerWorkflowType) => Promise<void>;
  forms: {
    getList: (
      args: GetListRequestType<
        typeof FormSubmissionGetListRequestSchema.shape.filters
      > & {
        workflowId: string;
      }
    ) => Promise<GetListResponseType<WorkflowFormSubmissionType>>;
  };
}
